// SubcategoryProducts.js

import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";

import Header from "../../components/header/Header";
import Subscribe from "../../components/subscribe/Subscribe";
import Footer from "../../components/footer/Footer";

import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import StarIcon from "@mui/icons-material/Star";

import { WishlistContext } from "../../context/WishlistContext";
import { CartContext } from "../../context/CartContext";

import { baseURL } from "../../constants/url";

import IconOne from "../../assets/images/category-1.svg";
import Watermark from "../../assets/images/no_product.jpg";

const SubcategoryProducts = () => {
  const { subcategoryId } = useParams();
  const navigate = useNavigate();

  // Context
  const { fetchWishlistCount } = useContext(WishlistContext);
  const { cartItems, fetchCartCount } = useContext(CartContext);

  // State
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  // Initial page loading
  const [loading, setLoading] = useState(false);

  // Load more loading
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);

  // Backend next page URL
  const [nextUrl, setNextUrl] = useState(null);

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  // ---------------------------------------------------------
  // Fetch Categories
  // ---------------------------------------------------------

  const fetchCategories = async () => {
    try {
      const res = await axios.get(
        `${baseURL}common/product_categories/`
      );
      console.log("response category", res);
      setCategories(res.data || []);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // ---------------------------------------------------------
  // Fetch First 50 Products
  // ---------------------------------------------------------

  useEffect(() => {
    const fetchProducts = async () => {
      if (!subcategoryId) return;

      setLoading(true);

      // Important when changing category
      setProducts([]);
      setNextUrl(null);

      try {
        const url =
          `${baseURL}common/products/?sub_category=${subcategoryId}`;

        const res = await axios.get(url);

        const data = res.data;

        console.log("Product API Response:", data);

        // Backend first page ke products
        setProducts(data?.results || []);

        // Backend next page
        setNextUrl(data?.next || null);

        console.log(
          "First Page Products:",
          data?.results?.length || 0
        );

        console.log(
          "Total Products:",
          data?.count
        );

        console.log(
          "Next Page URL:",
          data?.next
        );
      } catch (error) {
        console.error(
          "Error fetching products:",
          error
        );

        setProducts([]);
        setNextUrl(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [subcategoryId]);

  // ---------------------------------------------------------
  // Load Next 50 Products
  // ---------------------------------------------------------

  const loadMoreProducts = async () => {
    // No next page
    if (!nextUrl) return;

    // Prevent double click / multiple requests
    if (loadMoreLoading) return;

    setLoadMoreLoading(true);

    try {
      const res = await axios.get(nextUrl);

      const data = res.data;

      const newProducts = data?.results || [];

      console.log(
        "New Products Loaded:",
        newProducts.length
      );

      // Existing + new 50 products
      setProducts((prevProducts) => {
        return [
          ...prevProducts,
          ...newProducts,
        ];
      });

      // Store next page
      setNextUrl(data?.next || null);

      console.log(
        "Next Page URL:",
        data?.next
      );
    } catch (error) {
      console.error(
        "Error loading more products:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load more products.",
      });
    } finally {
      setLoadMoreLoading(false);
    }
  };

  // ---------------------------------------------------------
  // Add To Cart
  // ---------------------------------------------------------

  const addToCart = async (variant) => {
    if (!variant) return;

    if (variant.stock === 0) {
      return;
    }

    const token =
      localStorage.getItem("authToken");

    if (!token) {
      Swal.fire({
        icon: "warning",
        title: "Login Required",
        text: "Please login first",
        confirmButtonText: "Go to Login",
      }).then(() => {
        navigate("/login");
      });

      return;
    }

    const alreadyInCart =
      cartItems?.some(
        (item) =>
          item.variant_id === variant.id ||
          item.variant_id?.id === variant.id
      );

    if (alreadyInCart) {
      Swal.fire({
        icon: "info",
        title: "Already in Cart",
        text: "This product is already in your cart.",
        timer: 1500,
        showConfirmButton: false,
      });

      return;
    }

    try {
      await axios.post(
        `${baseURL}common/cart/add/`,
        {
          variant_id: variant.id,
          quantity: 1,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type":
              "application/json",
          },
        }
      );

      Swal.fire({
        icon: "success",
        title: "Added to Cart!",
        text: "The product has been added to your cart.",
        timer: 1500,
        showConfirmButton: false,
      });

      fetchCartCount();
    } catch (error) {
      console.error(
        "Cart Error:",
        error?.response?.data ||
          error.message
      );

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to add product to cart.",
      });
    }
  };

  // ---------------------------------------------------------
  // Toggle Wishlist
  // ---------------------------------------------------------

  const toggleWishlist = async (
    variantId
  ) => {
    const token =
      localStorage.getItem("authToken");

    if (!token) {
      Swal.fire({
        icon: "error",
        title: "Please Login",
        text: "Please log in to use wishlist.",
        confirmButtonText: "Go to Login",
      }).then(() => navigate("/login"));

      return;
    }

    try {
      const response =
        await axios.post(
          `${baseURL}common/wishlist_toggle/${variantId}/`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

      const isWishlisted =
        response?.data?.message
          ?.toLowerCase()
          .includes("added");

      Swal.fire({
        icon: isWishlisted
          ? "success"
          : "info",
        title: isWishlisted
          ? "Added to Wishlist"
          : "Removed from Wishlist",
        toast: true,
        position: "top-end",
        timer: 2000,
        showConfirmButton: false,
      });

      // Update wishlist state locally
      setProducts((prevProducts) =>
        prevProducts.map((product) => ({
          ...product,

          variants:
            product.variants?.map(
              (variant) =>
                variant.id === variantId
                  ? {
                      ...variant,
                      is_wishlisted:
                        isWishlisted,
                    }
                  : variant
            ) || [],
        }))
      );

      fetchWishlistCount();
    } catch (error) {
      console.error(
        "Error toggling wishlist:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Something went wrong!",
        text: "Please try again later.",
      });
    }
  };

  // ---------------------------------------------------------
  // UI
  // ---------------------------------------------------------

  return (
    <>
      <Header />

      {/* Initial Loading Overlay */}

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12 pt_20 mb_30">
            <div className="prod_list_grid">

              {/* ----------------------------- */}
              {/* Category List */}
              {/* ----------------------------- */}

              <div className="category-box">
                <h4>Categories</h4>

                {categories.map((cat) => (
                  <div
                    key={cat.id}
                    className={`category-item ${
                      selectedCategoryId ===
                      cat.id
                        ? "active"
                        : ""
                    }`}
                    onClick={() => {
                      setSelectedCategoryId(
                        cat.id
                      );

                      navigate(
                        `/subcategories/${cat.id}`
                      );
                    }}
                    style={{
                      cursor: "pointer",
                    }}
                  >
                    <div className="category-left">
                      <img
                        src={
                          cat.image ||
                          IconOne
                        }
                        alt={cat.name}
                        onError={(e) => {
                          e.currentTarget.src =
                            IconOne;
                        }}
                      />

                      <span>
                        {cat.name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* ----------------------------- */}
              {/* Products */}
              {/* ----------------------------- */}

              <div className="popular_products subcategory">

                <div className="popular_products_grid">

                  {loading ? (
                    <p className="text-center">
                      Loading...
                    </p>
                  ) : products.length > 0 ? (
                    products.map(
                      (product) => {
                        const variant =
                          product
                            .variants?.[0];

                        if (!variant) {
                          return null;
                        }

                        const isOutOfStock =
                          Number(
                            variant.stock
                          ) === 0;

                        const imagePath =
                          variant
                            ?.images?.[0]
                            ?.image;

                        return (
                          <div
                            key={
                              product.id
                            }
                            className="popular_products_grid_in"
                          >

                            {/* Product Header */}

                            <div className="products_head">

                              <div className="whislist_icon_box d_flex justify_sp_between">

                                {/* Wishlist */}

                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleWishlist(
                                      variant.id
                                    )
                                  }
                                  style={{
                                    background:
                                      "none",
                                    border:
                                      "none",
                                    cursor:
                                      "pointer",
                                  }}
                                >
                                  {variant.is_wishlisted ? (
                                    <FavoriteIcon
                                      style={{
                                        color:
                                          "red",
                                      }}
                                    />
                                  ) : (
                                    <FavoriteBorderIcon
                                      style={{
                                        color:
                                          "green",
                                      }}
                                    />
                                  )}
                                </button>

                                {/* Add To Cart */}

                                <button
                                  type="button"
                                  onClick={() =>
                                    addToCart(
                                      variant
                                    )
                                  }
                                  disabled={
                                    isOutOfStock
                                  }
                                  className="add_to_cart_icons_N mt_5 wishlist-btn font_size14"
                                  style={{
                                    background:
                                      "transparent",

                                    border:
                                      "none",

                                    cursor:
                                      isOutOfStock
                                        ? "not-allowed"
                                        : "pointer",

                                    opacity:
                                      isOutOfStock
                                        ? 0.4
                                        : 1,
                                  }}
                                >
                                  <AddShoppingCartIcon
                                    style={{
                                      color:
                                        "green",
                                    }}
                                  />
                                </button>

                              </div>
                            </div>

                            {/* Product Details */}

                            <div
                              className={`products_details ${
                                isOutOfStock
                                  ? "out-of-stock"
                                  : ""
                              }`}
                            >

                              {/* Image */}

                              <div className="products_img d_flex align_center">

                                <Link
                                  to={`/product-details/${product.id}`}
                                >
                                  <img
                                    src={
                                      imagePath ||
                                      IconOne
                                    }
                                    alt={
                                      product.name
                                    }
                                    onError={(
                                      e
                                    ) => {
                                      e.currentTarget.src =
                                        IconOne;
                                    }}
                                  />
                                </Link>

                                {isOutOfStock && (
                                  <span className="out_of_stock_badge">
                                    Out of
                                    Stock
                                  </span>
                                )}
                              </div>

                              {/* Product Information */}

                              <div className="products_info pt_10">

                                <div className="products_name mt_5">

                                  <Link
                                    to={`/product-details/${product.id}`}
                                  >
                                    {
                                      product.name
                                    }
                                  </Link>

                                </div>

                                {/* Price */}

                                <div className="pro_price mt_5 d_flex gap_10 align_center">

                                  <h6>
                                    ₹
                                    {
                                      variant.final_price
                                    }
                                    /-
                                  </h6>

                                  <h6 className="price_cut">
                                    <s>
                                      ₹
                                      {
                                        variant.price
                                      }
                                      /-
                                    </s>
                                  </h6>

                                  <h6 className="common_color">
                                    (
                                    {
                                      variant.discount_percent
                                    }
                                    % Off)
                                  </h6>

                                </div>

                                {/* Rating */}

                                <div className="rating_part d_flex align_center gap_5">

                                  <span>
                                    {variant.average_rating ||
                                      "0.0"}
                                  </span>

                                  <StarIcon />

                                </div>

                                {/* Buy Now */}

                                <div className="products_footer d_flex align_center justify_sp_center">

                                  {isOutOfStock ? (
                                    <span
                                      className="buy_btn font_size14 disabled-link"
                                      style={{
                                        background:
                                          "#f38c55",
                                        cursor:
                                          "not-allowed",
                                      }}
                                    >
                                      Out of
                                      Stock
                                    </span>
                                  ) : (
                                    <Link
                                      to={`/product-details/${product.id}`}
                                      className="buy_btn font_size14"
                                    >
                                      Buy Now
                                    </Link>
                                  )}

                                </div>

                              </div>
                            </div>
                          </div>
                        );
                      }
                    )
                  ) : (
                    <div className="container-fluid text-center pt_40 pb_40">

                      <img
                        src={Watermark}
                        alt="No Products Found"
                        style={{
                          width:
                            "300px",
                          opacity:
                            0.3,
                        }}
                      />

                      <h4 className="pt_20">
                        No Products
                        Available
                      </h4>

                    </div>
                  )}

                </div>

                {/* ----------------------------- */}
                {/* Load More */}
                {/* ----------------------------- */}

                {!loading &&
                  nextUrl && (
                    <div className="text-center mt_30 align_center d_flex justify_sp_center">

                      <button
                        type="button"
                        className="load_more d_flex align_center gap_30"
                        onClick={
                          loadMoreProducts
                        }
                        disabled={
                          loadMoreLoading
                        }
                      >

                        {loadMoreLoading
                          ? "Loading..."
                          : "Load More"}

                        {loadMoreLoading && (
                          <span className="loader"></span>
                        )}

                      </button>

                    </div>
                  )}

              </div>
            </div>
          </div>
        </div>
      </div>

      <Subscribe />

      <Footer />
    </>
  );
};

export default SubcategoryProducts;